<template>
  <div class="pricingpanel">
    <base-header class="pb-6">
      <div class="row py-4">
        <div class="col-7">
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <card no-body body-classes="px-0 pb-1" footer-classes="pb-2">
        <div slot="header" class="header_row">
          <h6 v-if="tableName" class="price_plan_name">{{ tableName }}</h6>
          <div v-if="checkNextPricePlan()" class="floatright">
            <button name="submit" class="btn greenbg btn-submit btn-vobo padded" size="vobo" @click="changePricePlanView()">
              {{ defaultView ? 'See Next
                            Available Price Plan' : 'Back' }}
            </button>
            <button :disabled="defaultView" name="submit" class="btn redbg btn-submit btn-vobo padded" size="vobo" @click="upgradeToPlan()">
              Upgrade to this Plan
            </button>
          </div>
        </div>
        <b-table
          v-if="pricePlan && Object.keys(pricePlan).length > 0"
          id="cppPricingFormTable"
          class="cppPricingFormTable"
          responsive
          hover
          striped
          :items="items"
          :fields="fields">
          <div slot="qty" slot-scope="item" class="data mb-3 has-feedback">
            <base-input
              v-model="item.item.qty"
              v-validate="'integer|min_value:1'"
              :data-vv-scope="item.item.field"
              placeholder="-"
              data-vv-name="qty"
              data-vv-as="qty"
              :class="{ input: true, 'is-invalid': errors.has('qty', item.item.field), 'has-invalid': errors.has('qty', item.item.field) }" />
            <span class="has-icon-feedback"></span>
            <div class="invalid-feedback" :is-invalid="errors.has('qty', item.item.field)">
              {{ errors.first('qty', item.item.field) }}
            </div>
          </div>
          <div slot="price" slot-scope="item" class="data">
            <label class="form-control-label" for="has_app_display_links_first">${{ item.item.qty * item.item.extra_bundle_price }} &nbsp &nbsp &nbsp</label>
            <button
              :disabled="item.item.add_automatically || !defaultView || errors.has('qty', item.item.field)"
              name="submit"
              class="btn primary btn-submit btn-vobo padded"
              size="vobo"
              @click="addBundle(item.item)">
              ADD
            </button>

            <button
              v-if="$store.state.user.userGroup === 4"
              :disabled="!defaultView || errors.has('qty', item.item.field)"
              name="submit"
              class="btn primary btn-submit btn-vobo padded"
              size="vobo"
              @click="addFreeBundle(item.item)">
              ADD FREE
            </button>
          </div>
          <div slot="add_automatically" slot-scope="item" class="data">
            <label class="form-control-label">Manual &nbsp &nbsp &nbsp</label>
            <base-switch v-model="item.item.add_automatically" class="mr-1 accessCode" on-text="" off-text="" @click.native="toggleAuto(item.item)">
            </base-switch>
            <label class="form-control-label">&nbsp &nbsp &nbsp Auto</label>
            <span class="has-icon-feedback"></span>
          </div>
        </b-table>
        <p v-else class="nopriceplan">No Price Plan for Selected Content Provider</p>
      </card>
      <router-link v-if="!backButtonDisabled" :to="computeBackURL()" class="btn btn-outline-vobo pr-5 pl-5 w-sm-100" @click.native="backButtonClicked()"
        >Back</router-link
      >
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import Cookies from 'js-cookie';
import {
  getContentProviderByID,
  forceUpdateContentProvider,
  purchaseAppUsers,
  purchaseOffAppTextCredits,
  upgradePricePlan,
  freeAppUsers,
  freeOffAppTextCredits,
} from '@/api/request/contentProvider';
import { getPricePlanById, getPricePlanList } from '@/api/request/pricePlans';
import { globalStore } from '@/utils/global';
import VeeValidate from 'vee-validate';
import { NotificationTypes, wrapNotification } from '@/utils/notification';

Vue.use(Cookies);

export default {
  data() {
    return {
      pricePlan: {},
      pricePlans: [],
      contentProvider: {},
      fields: [{ key: 'field', label: 'field' }, 'plan_allowance', 'extra_bundle', 'extra_bundle_price', 'qty', 'price', 'add_automatically'],
      items: [],
      nextPlan: {},
      defaultView: true,
      debounceSlider: false,
      tableName: null,
      backButtonDisabled: false,
    };
  },
  mounted() {
    const content_provider_id = this.$route.params.id;
    if (content_provider_id) {
      this.getContentProviderAndPricePlanData(content_provider_id);
      this.fetchPricePlansForOrg();
    }
  },
  methods: {
    backButtonClicked() {
      this.$store.dispatch('user/USER_PROFILE_REQUEST');
    },
    computeBackURL() {
      // client admin
      if (this.$store.state.user.userGroup !== 2) {
        return '/content-provider/list';
        // channel admin
      } else if (this.$store.state.user.userGroup === 2) {
        return '/dashboard';
      }
    },
    async getContentProviderAndPricePlanData(content_provider_id) {
      const token = Cookies.get('user-token') || globalStore.token;
      const content_provider = await getContentProviderByID(token, content_provider_id);
      this.contentProvider = content_provider.data.data;
      if (!this.contentProvider.purchased_app_users_cost) {
        this.contentProvider.purchased_app_users_cost = 0;
      }
      if (content_provider.data) {
        const { data } = await getPricePlanById(token, content_provider.data.data.pricePlan);
        this.pricePlan = data;
        this.buildTableItems(this.contentProvider, this.pricePlan);
      }
    },
    buildTableItems(contentProvider, pricePlan) {
      if (!contentProvider.auto_app_user_bundles) {
        contentProvider.auto_app_user_bundles = false;
      }
      if (!contentProvider.auto_off_app_text_credit_bundles) {
        contentProvider.auto_off_app_text_credit_bundles = false;
      }

      this.items = [
        {
          field: 'Off-App Text Credits',
          plan_allowance: pricePlan.off_app_text_credits_month,
          extra_bundle: pricePlan.extra_off_app_messages_bundle_credits,
          extra_bundle_price: pricePlan.extra_off_app_messages_bundle_charge,
          qty: 1,
          add_automatically: contentProvider.auto_off_app_text_credit_bundles,
        },
        {
          field: 'Max App Users',
          plan_allowance: pricePlan.max_app_users,
          extra_bundle: pricePlan.app_user_excess_bundle_count,
          extra_bundle_price: pricePlan.app_user_excess_bundle_monthly_charge,
          qty: 1,
          add_automatically: contentProvider.auto_app_user_bundles,
        },
      ];

      const purchased_app_users_cost = this.defaultView ? contentProvider.purchased_app_users_cost : 0;
      const monthlyCost = pricePlan.montly_charge + purchased_app_users_cost;
      this.tableName = pricePlan.plan_name + ' Price Plan $' + monthlyCost.toFixed(2) + '/mth';
    },
    async upgradeToPlan() {
      if (this.nextPlan) {
        let confirm = await this.$swal.fire({
          title: 'Confirm Upgrade',
          type: 'warning',
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          cancelButtonClass: 'btn btn-secondary pr-5 pl-5',
          confirmButtonClass: 'btn btn-danger pr-5 pl-5',
          reverseButtons: true,
          buttonsStyling: false,
          allowOutsideClick: false,
        });
        if (confirm.value) {
          const token = Cookies.get('user-token') || globalStore.token;
          const updated = await upgradePricePlan(token, { contentProvider: this.contentProvider }, this.contentProvider._id, this.nextPlan._id);

          if (updated) {
            wrapNotification(this.$notify, NotificationTypes.Success, 'Successfully Upgraded to Plan.');
          } else {
            wrapNotification(this.$notify, NotificationTypes.Error, 'Error Upgrading Plan.');
          }
          this.getContentProviderAndPricePlanData(updated.data.data._id);
          this.fetchPricePlansForOrg();
          this.changePricePlanView();
        }
      }
    },
    async addBundle(item) {
      let confirm = await this.$swal.fire({
        title: 'Confirm Purchase',
        type: 'warning',
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        cancelButtonClass: 'btn btn-secondary pr-5 pl-5',
        confirmButtonClass: 'btn btn-danger pr-5 pl-5',
        reverseButtons: true,
        buttonsStyling: false,
        allowOutsideClick: false,
      });
      if (confirm.value) {
        // unused until pricing is implemented
        const price = item.price;

        const amount_to_update = item.extra_bundle * item.qty;
        let update = this.contentProvider;

        const token = Cookies.get('user-token') || globalStore.token;
        let updated = undefined;

        this.backButtonDisabled = true;

        if (item.field === 'Off-App Text Credits') {
          updated = await purchaseOffAppTextCredits(token, { contentProvider: update }, this.contentProvider._id, amount_to_update);
        } else if (item.field === 'Max App Users') {
          updated = await purchaseAppUsers(token, { contentProvider: update }, this.contentProvider._id, amount_to_update);
        }

        if (updated) {
          wrapNotification(this.$notify, NotificationTypes.Success, 'Successfully Purchased Bundle.');
        } else {
          wrapNotification(this.$notify, NotificationTypes.Error, 'Error Purchasing Bundle.');
        }
        this.backButtonDisabled = false;
        this.getContentProviderAndPricePlanData(this.contentProvider._id);
      }
    },
    async addFreeBundle(item) {
      let confirm = await this.$swal.fire({
        title: 'Add Free Bundle to channel',
        type: 'warning',
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        cancelButtonClass: 'btn btn-secondary pr-5 pl-5',
        confirmButtonClass: 'btn btn-danger pr-5 pl-5',
        reverseButtons: true,
        buttonsStyling: false,
        allowOutsideClick: false,
      });
      if (confirm.value) {
        const amount_to_update = item.extra_bundle * item.qty;

        const token = Cookies.get('user-token') || globalStore.token;
        let updated = undefined;

        this.backButtonDisabled = true;
        if (item.field === 'Off-App Text Credits') {
          updated = await freeOffAppTextCredits(token, this.contentProvider._id, amount_to_update);
        } else if (item.field === 'Max App Users') {
          updated = await freeAppUsers(token, this.contentProvider._id, amount_to_update);
        }

        if (updated) {
          wrapNotification(this.$notify, NotificationTypes.Success, 'Successfully Added Free Bundle.');
        } else {
          wrapNotification(this.$notify, NotificationTypes.Error, 'Error Adding Free Bundle.');
        }
        this.backButtonDisabled = false;
        this.getContentProviderAndPricePlanData(this.contentProvider._id);
      }
    },
    async fetchPricePlansForOrg() {
      const token = Cookies.get('user-token') || globalStore.token;
      const company_id = Cookies.get('companyID');
      const pricePlans = await getPricePlanList(token, company_id);
      if (pricePlans) {
        this.pricePlans = pricePlans.data;
      }
    },
    checkNextPricePlan() {
      const pplan = this.pricePlan;
      const pplans = this.pricePlans;
      if (!pplan.next_superior_plan_name) {
        return false;
      }
      if (pplan.next_superior_plan_name.length < 1) {
        return false;
      }
      let super_plan = pplans.find((plan) => plan['plan_name'] === pplan.next_superior_plan_name);
      this.nextPlan = super_plan;
      if (super_plan) {
        return true;
      } else {
        return false;
      }
    },
    changePricePlanView() {
      if (this.defaultView) {
        this.defaultView = !this.defaultView;
        this.buildTableItems(this.contentProvider, this.nextPlan);
      } else {
        this.defaultView = !this.defaultView;
        this.buildTableItems(this.contentProvider, this.pricePlan);
      }
    },
    async toggleAuto(item) {
      if (!this.debounceSlider) {
        this.debounceSlider = true;
        const confirm = await this.$swal.fire({
          title: 'Confirm Switch',
          text: `${item.field} will be added: ${!item.add_automatically ? 'Automatically' : 'Manually'} `,
          type: 'warning',
          showConfirmButton: true,
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          cancelButtonClass: 'btn btn-secondary pr-5 pl-5',
          confirmButtonClass: 'btn btn-danger pr-5 pl-5',
          reverseButtons: true,
          buttonsStyling: false,
          allowOutsideClick: false,
        });
        if (confirm.value) {
          const token = Cookies.get('user-token') || globalStore.token;

          let update = this.contentProvider;
          if (item.field === 'Off-App Text Credits') {
            update.auto_off_app_text_credit_bundles = item.add_automatically;
          } else if (item.field === 'Max App Users') {
            update.auto_app_user_bundles = item.add_automatically;
          }

          const updated = await forceUpdateContentProvider(token, { contentProvider: update }, this.contentProvider._id);

          if (updated) {
            wrapNotification(this.$notify, NotificationTypes.Success, 'Successfully Changed Add Automatically.');
          } else {
            wrapNotification(this.$notify, NotificationTypes.Error, 'Error Changing Add Automatically.');
          }
        } else {
          item.add_automatically = !item.add_automatically;
        }
        this.debounceSlider = false;
      }
    },
  },
};
</script>
<style lang="scss">
.greenbg {
  background-color: green;
  border: none;
  width: 15rem;
  height: 2.5rem;
  padding: 1% !important;
}

.redbg {
  background-color: red;
  border: none;
  width: 15rem;
  height: 2.5rem;
  padding: 1% !important;
}

.floatright {
  margin-top: 0.5rem;
  margin-right: 0.5rem;
  margin-bottom: 1rem;
}

.pricingpanel {
  margin-bottom: 1rem;
}

.cppPricingFormTable {
  text-align: center;
  table-layout: fixed;
  width: 100%;
  padding-left: 2%;
  padding-right: 2%;
}

.cppPricingFormTable th,
td {
  word-wrap: break-word;
  overflow-x: auto;
  overflow-y: auto;
}

.cppPricingFormTable th {
  color: #8898aa;
  background-color: #ebeff3 !important;
}

.topPricingPanel {
  display: flex;
  justify-content: space-between;
}

.price_plan_name {
  font-size: 1.0625rem;
  width: 30%;
  align-self: center;
}

.header_row {
  display: flex;
  justify-content: space-between;
}

.card-header {
  padding-top: 0.5rem !important;
  padding-bottom: 0rem !important;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  background-color: #f7f9fa;
}

.nopriceplan {
  padding: 3%;
  text-align: center;
  font-size: 2rem;
  font-weight: 900;
}
</style>
